import {
  Alert,
  Button,
  Collapse,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Table,
} from 'antd'
import { getPagination, getTableFilter, handleUrlChange } from '@TerraCycleUS/loop-antd-filter'
import dayjs from 'dayjs'
import { orderBy } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { styled } from 'styled-components'
import { Can } from '../'
import { handleBackendError, trimFields } from '../../utils'

import { getUserTransactions } from '../../pages/users/api'
import { createBalanceAdjustment } from './api'

const { TextArea } = Input

const FieldGroup = styled.div`
  margin: 20px 0;

  &:first-child {
    margin-top: 0px;
  }
`

const Label = styled.div`
  margin: 3px 0;
`

const FieldValue = styled.div`
  margin: 3px 0;

  font-weight: bold;
`

const TransactionHistoryModal = ({
  config,
  errorMessage,
  hideModal,
  selectedUser,
}) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(true)
  const [userTransactions, setUserTransactions] = useState([])
  const [metaData, setMetaData] = useState()
  const hasAppliedOwnParams = useRef(false)

  const typeFilters = orderBy(
    config.config?.transaction_types.map((type) => ({
      text: type,
      value: type,
    })),
    'text',
    'asc',
  )
  const statusFilters = orderBy(
    config.config?.transaction_statuses.map((status) => ({
      text: status,
      value: status,
    })),
    'text',
    'asc',
  )
  const categoryFilters = orderBy(
    config.config?.transaction_categories.map((type) => ({
      text: type.split('::')[1],
      value: type,
    })),
    'text',
    'asc',
  )

  const isZeroDecimal = selectedUser.country_locales[0] === 'ja-JP'

  const fetchTransactions = (params) => {
    setIsLoading(true)

    getUserTransactions(
      selectedUser.id,
      params,
    )
      .then(({ data }) => {
        setMetaData(data.meta)
        setUserTransactions(data.data)
        setIsLoading(false)
      })
      .catch((error) => {
        notification.error({
          description: handleBackendError(
            error,
            'Fetching transaction history failed!',
          ),
          title: 'Error',
        })
      })
  }

  useEffect(() => {
    const previousParams = searchParams.toString()

    const defaultParams = {
      items_per_page: 10,
      page: 1,
      sort_by: 'created_at DESC',
    }

    setSearchParams(defaultParams)
    fetchTransactions(new URLSearchParams(defaultParams).toString())

    return () => setSearchParams(new URLSearchParams(previousParams))
  }, [])

  useEffect(() => {
    if (!hasAppliedOwnParams.current) {
      hasAppliedOwnParams.current = true
      return
    }

    if (searchParams.get('page') === 'null') return

    fetchTransactions(searchParams.toString())
  }, [searchParams, selectedUser])

  const handleSubmit = () => {
    const isFormUpdated = form.isFieldsTouched()

    if (isFormUpdated) {
      form.validateFields().then(() => {
        setIsLoading(true)

        const fieldsValue = trimFields(form.getFieldsValue([
          'amount',
          'comment',
        ]))

        const requiredFieldsValue = {
          ...fieldsValue,
          user_id: selectedUser.id,
        }

        createBalanceAdjustment(requiredFieldsValue)
          .then(() => {
            notification.success({
              description: 'Balance adjustment complete!',
              title: 'Success',
            })

            hideModal()
          })
          .catch((error) => {
            setIsLoading(false)

            notification.error({
              description: handleBackendError(
                error,
                'Creating balance adjustment failed!',
              ),
              title: 'Error',
            })
          })
      })
    }
  }

  const tableColumns = [
    {
      className: 'transactionCategorySelenium',
      dataIndex: 'category',
      render: (_, record) => <span>{record.category?.split('::')[1]}</span>,
      sorter: true,
      title: 'Category',
      ...getTableFilter({
        filterValues: categoryFilters,
        isMultiple: false,
        name: 'category',
        type: 'checkbox',
      }),
    },
    {
      className: 'transactionTypeSelenium',
      dataIndex: 'type',
      render: (_, record) => <span>{record.type}</span>,
      sorter: true,
      title: 'Type',
      ...getTableFilter({
        filterValues: typeFilters,
        isMultiple: false,
        name: 'type',
        type: 'checkbox',
      }),
    },
    {
      className: 'transactionStatusSelenium',
      dataIndex: 'status',
      render: (_, record) => record.status || 'N/A',
      sorter: true,
      title: 'Status',
      ...getTableFilter({
        filterValues: statusFilters,
        isMultiple: false,
        name: 'status',
        type: 'checkbox',
      }),
    },
    {
      className: 'transactionCreatedAtSelenium',
      dataIndex: 'created_at',
      defaultSortOrder: 'descend',
      render: (_, record) => dayjs(record.created_at).format('DD.MM.YYYY'),
      sorter: true,
      title: 'Created at',
      ...getTableFilter({ name: 'created_at', type: 'date' }),
    },
    {
      className: 'transactionUpdatedAtSelenium',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (_, record) => dayjs(record.updated_at).format('DD.MM.YYYY'),
      sorter: true,
      title: 'Updated at',
      ...getTableFilter({ name: 'updated_at', type: 'date' }),
    },
    {
      className: 'transactionAmountSelenium',
      dataIndex: 'amount',
      key: 'amount',
      render: (_, record) =>
        new Intl.NumberFormat(selectedUser.country_locales[0], {
          currency: selectedUser.currency_symbol,
          style: 'currency',
        }).format(record.amount),
      sorter: true,
      title: 'Amount',
    },
    {
      className: 'transactionNoteSelenium',
      dataIndex: 'comment',
      key: 'note',
      title: 'Note',
    },
  ]

  const BalanceAdjustmentPanel = () => (
    <Form
      data-testid="balanceAdjustmentForm"
      form={form}
      layout="vertical"
      onFinish={handleSubmit}
    >
      <Form.Item
        label="Amount"
        name="amount"
        rules={[{ message: 'Please adjust User\'s balance!', required: true }]}
      >
        <InputNumber
          addonBefore={selectedUser.currency_symbol.toUpperCase()}
          data-testid="amount"
          precision={isZeroDecimal ? 0 : 2}
          style={{ width: '100%' }}
          type="number"
        />
      </Form.Item>

      <Form.Item label="Comment (will be visible for the user)" name="comment">
        <TextArea />
      </Form.Item>

      <Form.Item>
        <Button htmlType="submit" name="saveButtonSelenium" type="primary">
          Save
        </Button>
      </Form.Item>
    </Form>
  )

  return (
    <Modal
      data-testid="transacionModal"
      footer={[]}
      onCancel={hideModal}
      open
      title={`${selectedUser.name} transaction history`}
      width={1000}
    >
      {selectedUser.status !== 'inactive' &&
        selectedUser.user_type === 'regular' && (
        <Can
          requiredPermission="adjustment.post.create"
          yes={() => (
            <Collapse
              items={[
                {
                  children: <BalanceAdjustmentPanel />,
                  key: '1',
                  label: 'Balance Adjustment',
                },
              ]}
            />
          )}
        />
      )}

      <FieldGroup name="userName">
        <Label>User name</Label>
        <FieldValue>{selectedUser.name}</FieldValue>
      </FieldGroup>

      <FieldGroup name="userId">
        <Label>User id</Label>
        <FieldValue>{selectedUser.id}</FieldValue>
      </FieldGroup>

      <FieldGroup name="email">
        <Label>E-mail</Label>
        <FieldValue>{selectedUser.email}</FieldValue>
      </FieldGroup>

      <FieldGroup name="country">
        <Label>Country</Label>
        <FieldValue>{selectedUser.country_name}</FieldValue>
      </FieldGroup>

      <FieldGroup name="balance">
        <Label>Balance</Label>
        <FieldValue>
          {new Intl.NumberFormat(selectedUser.country_locales[0], {
            currency: selectedUser.currency_symbol,
            style: 'currency',
          }).format(selectedUser.balance)}
        </FieldValue>
      </FieldGroup>

      <Table
        className="usersTransactionHistoryTableSelenium"
        columns={tableColumns}
        dataSource={userTransactions}
        isModalTable={true}
        loading={isLoading}
        onChange={(pagination, filters, sorter) => handleUrlChange(pagination, filters, sorter, setSearchParams)}
        pagination={getPagination(metaData?.page, metaData?.items_per_page, metaData?.total_items)}
      />

      {errorMessage && (
        <Alert
          description={errorMessage}
          showIcon
          style={{ marginTop: 20 }}
          title="Error"
          type="error"
        />
      )}
    </Modal>
  )
}

TransactionHistoryModal.propTypes = {
  config: PropTypes.object.isRequired,
  errorMessage: PropTypes.string,
  hideModal: PropTypes.func.isRequired,
  selectedUser: PropTypes.object,
}

export default TransactionHistoryModal
